import Phaser from 'phaser';
import { MusicManager } from './MusicManager';
import { isMobileOrTablet } from '../utils/DeviceUtils';

// === GESTIONNAIRE DE VISIBILITÉ (PAUSE/REPRISE) ===
// Sur mobile, le jeu peut être mis en arrière-plan à tout moment.
// Ce manager écoute 'visibilitychange' pour :
// - Mettre le jeu en pause et couper le son quand l'onglet est masqué
// - Reprendre le jeu et le son quand l'onglet redevient visible
// - Sauvegarder l'état du run en cours si applicable

export class VisibilityManager {
  private static game: Phaser.Game | null = null;
  private static boundHandler: (() => void) | null = null;
  private static wasMusicEnabled = false;
  private static resumeBanner: HTMLDivElement | null = null;

  /** Initialiser le gestionnaire — appeler une fois après new Phaser.Game() */
  static init(game: Phaser.Game): void {
    VisibilityManager.game = game;

    VisibilityManager.boundHandler = VisibilityManager.onVisibilityChange.bind(VisibilityManager);
    document.addEventListener('visibilitychange', VisibilityManager.boundHandler);
  }

  private static onVisibilityChange(): void {
    const game = VisibilityManager.game;
    if (!game) return;

    if (document.hidden) {
      VisibilityManager.onHidden(game);
    } else {
      VisibilityManager.onVisible(game);
    }
  }

  /** Quand l'onglet/app passe en arrière-plan */
  private static onHidden(game: Phaser.Game): void {
    // Couper le son (Phaser gère aussi ça, mais on s'assure)
    VisibilityManager.wasMusicEnabled = MusicManager.isEnabled();
    game.sound.pauseAll();

    // Pause toutes les scènes actives
    const activeScenes = game.scene.getScenes(true);
    for (const scene of activeScenes) {
      scene.scene.pause();
    }

    // Sur mobile, sauvegarder le run en cours si possible
    if (isMobileOrTablet()) {
      VisibilityManager.trySaveCurrentRun(game);
    }
  }

  /** Quand l'onglet/app revient au premier plan */
  private static onVisible(game: Phaser.Game): void {
    // Reprendre les scènes
    const activeScenes = game.scene.getScenes(true);
    for (const scene of activeScenes) {
      scene.scene.resume();
    }

    // Reprendre le son
    game.sound.resumeAll();

    // Afficher brièvement un indicateur "Jeu repris" sur mobile
    if (isMobileOrTablet()) {
      VisibilityManager.showResumeBanner();
    }
  }

  /**
   * Tente de sauvegarder le run en cours via RunManager.
   *
   * **Important** : on n'écrase JAMAIS la sauvegarde si l'utilisateur est en
   * plein combat. Le combat est atomique (soit complété → loot + commit, soit
   * abandonné → on perd ce qui a été consommé). Sauver mid-combat capturerait
   * l'inventaire post-conso (ex : potion utilisée) sans avoir validé l'issue
   * du combat → au reload, le combat reset mais le consommable reste perdu.
   * La sauvegarde pré-combat (faite par `selectMapNode()`) reste intacte et
   * sera restaurée au reload, avec tous les consommables.
   */
  private static SCENES_NOT_COMMITTABLE = new Set(['CombatScene']);

  private static trySaveCurrentRun(game: Phaser.Game): void {
    try {
      const activeScenes = game.scene.getScenes(true);
      // Si une scène non-committable est active, on skip — la sauvegarde
      // pré-action reste valide (cf. selectMapNode → saveRunState).
      for (const scene of activeScenes) {
        if (VisibilityManager.SCENES_NOT_COMMITTABLE.has(scene.scene.key)) {
          return;
        }
      }
      for (const scene of activeScenes) {
        const run = (scene as any).run;
        if (run && typeof run.saveRunState === 'function') {
          run.saveRunState();
          break;
        }
      }
    } catch {
      // Pas de run en cours ou erreur de sauvegarde — ignoré
    }
  }

  /** Affiche un bandeau "Jeu repris" qui disparaît après 1.5s */
  private static showResumeBanner(): void {
    // Réutiliser le bandeau existant s'il y en a un
    if (VisibilityManager.resumeBanner) {
      VisibilityManager.resumeBanner.remove();
    }

    const banner = document.createElement('div');
    banner.textContent = '▶ Jeu repris';
    banner.style.cssText = `
      position: fixed;
      top: 10px;
      left: 50%;
      transform: translateX(-50%);
      background: rgba(0, 0, 0, 0.7);
      color: #f1c40f;
      font-family: monospace;
      font-size: 14px;
      padding: 6px 16px;
      border-radius: 4px;
      z-index: 10000;
      pointer-events: none;
      transition: opacity 0.5s;
    `;
    document.body.appendChild(banner);
    VisibilityManager.resumeBanner = banner;

    // Fade out puis suppression
    setTimeout(() => {
      banner.style.opacity = '0';
      setTimeout(() => {
        banner.remove();
        if (VisibilityManager.resumeBanner === banner) {
          VisibilityManager.resumeBanner = null;
        }
      }, 500);
    }, 1500);
  }

  /** Nettoyer le listener (si besoin) */
  static destroy(): void {
    if (VisibilityManager.boundHandler) {
      document.removeEventListener('visibilitychange', VisibilityManager.boundHandler);
      VisibilityManager.boundHandler = null;
    }
    VisibilityManager.game = null;
  }
}
